import React, { useState } from 'react';
import { Search, MessageSquare, AlertTriangle, Bell, CheckCircle2, Clock, Send, User, Filter, MoreVertical, Paperclip, Plus, Star, Check, Archive } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
interface Ticket {
  id: string;
  subject: string;
  user: string;
  priority: string;
  status: string;
  date: string;
}
interface AdminMessagingProps {
  tickets: Ticket[];
  setTickets: React.Dispatch<React.SetStateAction<Ticket[]>>;
  success: (msg: string) => void;
}
export function AdminMessaging({
  tickets,
  setTickets,
  success
}: AdminMessagingProps) {
  const [activeTab, setActiveTab] = useState('support');
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all'
  });
  const tabs = [{
    id: 'support',
    label: 'Поддержка',
    icon: <MessageSquare className="w-4 h-4" data-id="element-5196" />
  }, {
    id: 'complaints',
    label: 'Жалобы',
    icon: <AlertTriangle className="w-4 h-4" data-id="element-5197" />
  }, {
    id: 'notifications',
    label: 'Рассылки',
    icon: <Bell className="w-4 h-4" data-id="element-5198" />
  }];
  const filteredTickets = tickets.filter(t => {
    const matchesSearch = t.subject.toLowerCase().includes(searchQuery.toLowerCase()) || t.user.toLowerCase().includes(searchQuery.toLowerCase());
    if (searchQuery && !matchesSearch) return false;
    if (filters.status !== 'all' && t.status !== filters.status) return false;
    if (filters.priority !== 'all' && t.priority !== filters.priority) return false;
    if (activeTab === 'support') return true; // Для демо показываем все в поддержке
    if (activeTab === 'complaints') return t.priority === 'high';
    return false;
  });
  const activeTicket = tickets.find(t => t.id === activeTicketId);
  const handleSendMessage = () => {
    if (!messageText.trim()) return;
    success('Сообщение отправлено');
    setMessageText('');
  };
  const handleCloseTicket = (id: string) => {
    setTickets(tickets.map(t => t.id === id ? {
      ...t,
      status: 'closed'
    } : t));
    success('Тикет закрыт');
  };
  return <div className="space-y-4 h-[calc(100vh-10rem)] min-h-[600px] flex flex-col" data-id="element-5199">
      <div className="flex items-center justify-between" data-id="element-5200">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-5201">Коммуникации</h2>
        <Button icon={<Plus className="w-4 h-4" data-id="element-5203" />} data-id="element-5202">Новое сообщение</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 flex-shrink-0" data-id="element-5204">
        <Card padding="sm" className="flex items-center gap-3" data-id="element-5205">
          <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center text-rose-600" data-id="element-5206">
            <AlertTriangle className="w-5 h-5" data-id="element-5207" />
          </div>
          <div data-id="element-5208">
            <div className="text-2xl font-bold text-slate-900" data-id="element-5209">
              {tickets.filter(t => t.status === 'open').length}
            </div>
            <div className="text-xs text-slate-500" data-id="element-5210">Открытых тикетов</div>
          </div>
        </Card>
        <Card padding="sm" className="flex items-center gap-3" data-id="element-5211">
          <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600" data-id="element-5212">
            <Clock className="w-5 h-5" data-id="element-5213" />
          </div>
          <div data-id="element-5214">
            <div className="text-2xl font-bold text-slate-900" data-id="element-5215">14 мин</div>
            <div className="text-xs text-slate-500" data-id="element-5216">Среднее время ответа</div>
          </div>
        </Card>
        <Card padding="sm" className="flex items-center gap-3" data-id="element-5217">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-5218">
            <CheckCircle2 className="w-5 h-5" data-id="element-5219" />
          </div>
          <div data-id="element-5220">
            <div className="text-2xl font-bold text-slate-900" data-id="element-5221">98%</div>
            <div className="text-xs text-slate-500" data-id="element-5222">Решенных вопросов</div>
          </div>
        </Card>
        <Card padding="sm" className="flex items-center gap-3" data-id="element-5223">
          <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600" data-id="element-5224">
            <Star className="w-5 h-5" data-id="element-5225" />
          </div>
          <div data-id="element-5226">
            <div className="text-2xl font-bold text-slate-900" data-id="element-5227">4.9</div>
            <div className="text-xs text-slate-500" data-id="element-5228">Оценка поддержки</div>
          </div>
        </Card>
      </div>

      <Card padding="none" className="flex-grow flex overflow-hidden border border-slate-200" data-id="element-5229">
        {/* Sidebar */}
        <div className="w-full md:w-1/3 border-r border-slate-200 flex flex-col bg-slate-50/50" data-id="element-5230">
          <div className="p-3 border-b border-slate-200 bg-white" data-id="element-5231">
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg mb-3" data-id="element-5232">
              {tabs.map(tab => <button key={tab.id} onClick={() => {
              setActiveTab(tab.id);
              setActiveTicketId(null);
            }} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`} data-id="element-5233">
                  {tab.icon}
                  <span className="hidden xl:inline" data-id="element-5234">{tab.label}</span>
                </button>)}
            </div>
            <div className="flex gap-2" data-id="element-5235">
              <div className="flex-1" data-id="element-5236">
                <Input placeholder="Поиск..." icon={<Search className="w-4 h-4" data-id="element-5238" />} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="mb-0" data-id="element-5237" />
              </div>
              <Button variant={showFilters ? 'primary' : 'outline'} className="px-3" icon={<Filter className="w-4 h-4" data-id="element-5240" />} onClick={() => setShowFilters(!showFilters)} data-id="element-5239" />
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && <div className="p-3 border-b border-slate-200 bg-white space-y-3 animate-in slide-in-from-top-2" data-id="element-5241">
              <div data-id="element-5242">
                <label className="block text-[10px] font-medium text-slate-500 mb-1 uppercase tracking-wider" data-id="element-5243">
                  Статус
                </label>
                <select className="w-full rounded-md border border-slate-200 bg-white text-xs py-1.5 px-2 focus:outline-none focus:border-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-5244">
                  <option value="all" data-id="element-5245">Все</option>
                  <option value="open" data-id="element-5246">Открыт</option>
                  <option value="closed" data-id="element-5247">Закрыт</option>
                </select>
              </div>
              <div data-id="element-5248">
                <label className="block text-[10px] font-medium text-slate-500 mb-1 uppercase tracking-wider" data-id="element-5249">
                  Приоритет
                </label>
                <select className="w-full rounded-md border border-slate-200 bg-white text-xs py-1.5 px-2 focus:outline-none focus:border-indigo-500" value={filters.priority} onChange={e => setFilters({
              ...filters,
              priority: e.target.value
            })} data-id="element-5250">
                  <option value="all" data-id="element-5251">Все</option>
                  <option value="high" data-id="element-5252">Высокий</option>
                  <option value="normal" data-id="element-5253">Средний</option>
                  <option value="low" data-id="element-5254">Низкий</option>
                </select>
              </div>
              <div className="pt-1" data-id="element-5255">
                <Button variant="ghost" size="sm" className="w-full text-xs h-7" onClick={() => setFilters({
              status: 'all',
              priority: 'all'
            })} data-id="element-5256">
                  Сбросить
                </Button>
              </div>
            </div>}

          <div className="flex-grow overflow-y-auto" data-id="element-5257">
            {filteredTickets.length === 0 ? <div className="p-8 text-center text-slate-500" data-id="element-5258">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" data-id="element-5259" />
                <p className="text-sm" data-id="element-5260">Нет диалогов</p>
              </div> : <div className="divide-y divide-slate-100" data-id="element-5261">
                {filteredTickets.map(ticket => <div key={ticket.id} onClick={() => setActiveTicketId(ticket.id)} className={`p-3 sm:p-4 cursor-pointer hover:bg-slate-100 transition-colors ${activeTicketId === ticket.id ? 'bg-white border-l-2 border-l-primary' : ''}`} data-id="element-5262">
                    <div className="flex justify-between items-start mb-1" data-id="element-5263">
                      <div className="flex items-center gap-2" data-id="element-5264">
                        <span className="text-xs font-bold text-slate-900 truncate max-w-[150px]" data-id="element-5265">
                          {ticket.user}
                        </span>
                        {ticket.priority === 'high' && <span className="w-2 h-2 rounded-full bg-rose-500" data-id="element-5266"></span>}
                      </div>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap" data-id="element-5267">
                        {ticket.date}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-slate-800 mb-1 truncate" data-id="element-5268">
                      {ticket.subject}
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5269">
                      <span className="text-xs text-slate-500 truncate pr-2" data-id="element-5270">
                        {ticket.status === 'open' ? 'Ожидает ответа...' : 'Вопрос решен'}
                      </span>
                      <Badge variant={ticket.status === 'open' ? 'warning' : 'neutral'} size="sm" data-id="element-5271">
                        {ticket.status === 'open' ? 'Открыт' : 'Закрыт'}
                      </Badge>
                    </div>
                  </div>)}
              </div>}
          </div>
        </div>

        {/* Chat Area */}
        <div className="hidden md:flex flex-col w-2/3 bg-white" data-id="element-5272">
          {!activeTicket ? <div className="flex-grow flex flex-col items-center justify-center text-slate-400" data-id="element-5273">
              <MessageSquare className="w-12 h-12 text-slate-200 mb-3" data-id="element-5274" />
              <div className="text-sm font-medium" data-id="element-5275">Выберите диалог</div>
              <div className="text-xs text-slate-300 mt-1" data-id="element-5276">
                Нажмите на тикет слева, чтобы начать общение
              </div>
            </div> : <>
              {/* Chat Header */}
              <div className="p-4 border-b border-slate-100 flex justify-between items-start" data-id="element-5277">
                <div data-id="element-5278">
                  <div className="flex items-center gap-3 mb-1" data-id="element-5279">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-5280">
                      {activeTicket.subject}
                    </h3>
                    <Badge variant={activeTicket.priority === 'high' ? 'error' : 'neutral'} size="sm" data-id="element-5281">
                      {activeTicket.priority === 'high' ? 'Высокий приоритет' : 'Обычный'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-500" data-id="element-5282">
                    <span className="flex items-center gap-1.5" data-id="element-5283">
                      <User className="w-4 h-4" data-id="element-5284" /> {activeTicket.user}
                    </span>
                    <span className="flex items-center gap-1.5" data-id="element-5285">
                      <Clock className="w-4 h-4" data-id="element-5286" /> {activeTicket.date}
                    </span>
                    <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600" data-id="element-5287">
                      {activeTicket.id}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-5288">
                  {activeTicket.status === 'open' ? <Button variant="outline" size="sm" icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5290" />} onClick={() => handleCloseTicket(activeTicket.id)} data-id="element-5289">
                      Закрыть тикет
                    </Button> : <Button variant="outline" size="sm" icon={<Archive className="w-4 h-4" data-id="element-5292" />} data-id="element-5291">
                      В архив
                    </Button>}
                  <Button variant="ghost" size="sm" className="px-2" data-id="element-5293">
                    <MoreVertical className="w-4 h-4 text-slate-400" data-id="element-5294" />
                  </Button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-slate-50/30" data-id="element-5295">
                {/* User Message */}
                <div className="flex gap-3 max-w-[80%]" data-id="element-5296">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold flex-shrink-0 text-xs" data-id="element-5297">
                    {activeTicket.user.substring(0, 2).toUpperCase()}
                  </div>
                  <div data-id="element-5298">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none p-3 shadow-sm" data-id="element-5299">
                      <p className="text-sm text-slate-700" data-id="element-5300">
                        Здравствуйте! У меня возникла проблема:{' '}
                        {activeTicket.subject.toLowerCase()}. Можете помочь
                        разобраться? Я уже пробовал перезагрузить страницу, но
                        это не помогло.
                      </p>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1 ml-1" data-id="element-5301">
                      {activeTicket.date}
                    </div>
                  </div>
                </div>

                {/* System Message (if closed) */}
                {activeTicket.status === 'closed' && <div className="flex justify-center my-4" data-id="element-5302">
                    <div className="bg-slate-100 text-slate-500 text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5" data-id="element-5303">
                      <Check className="w-3 h-3" data-id="element-5304" /> Тикет закрыт администратором
                    </div>
                  </div>}
              </div>

              {/* Chat Input */}
              {activeTicket.status === 'open' && <div className="p-4 border-t border-slate-100 bg-white" data-id="element-5305">
                  <div className="flex items-center gap-2 mb-2" data-id="element-5306">
                    <span className="text-xs font-medium text-slate-500" data-id="element-5307">
                      Быстрые ответы:
                    </span>
                    <button className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded transition-colors" data-id="element-5308">
                      Приветствие
                    </button>
                    <button className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded transition-colors" data-id="element-5309">
                      Запрос деталей
                    </button>
                    <button className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded transition-colors" data-id="element-5310">
                      Проблема решена
                    </button>
                  </div>
                  <div className="flex items-end gap-2" data-id="element-5311">
                    <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors mb-1" data-id="element-5312">
                      <Paperclip className="w-5 h-5" data-id="element-5313" />
                    </button>
                    <textarea value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Написать ответ пользователю..." className="flex-grow min-h-[80px] max-h-[150px] resize-y rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }} data-id="element-5314" />
                    <Button variant="primary" className="mb-1" disabled={!messageText.trim()} onClick={handleSendMessage} data-id="element-5315">
                      <Send className="w-4 h-4" data-id="element-5316" />
                    </Button>
                  </div>
                </div>}
            </>}
        </div>
      </Card>
    </div>;
}